import { IonFab, IonFabButton, IonIcon } from '@ionic/react';
import { FieldArray, FormikProvider } from 'formik';
import { closeCircleOutline } from 'ionicons/icons';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useProductForm } from '../../../../api/hooks/products/useProductForm';
import {
  getProdCharacterizationAadeCodeToSet,
  isCharValidForDocType,
} from '../../../../services/utils/docTypeCharacterizationsMap';
import { DocProductDescription } from './DocProductDescription';
import { DocProductDiscount } from './DocProductDiscount';
import { DocProductFinalCost } from './DocProductFinalCost';
import { DocProductPrice } from './DocProductPrice';
import { DocProductQuantity } from './DocProductQuantity';
import { DocProductSelect } from './DocProductSelect';
import { DocProductUnit } from './DocProductUnit';
import { DocProductVat } from './DocProductVat';

interface Props {
  index: number;
  formik: any;
  isSectionDisabled?: boolean;
  docTypeAadeCode: string;
  debugMode?: boolean;
}

export const DocProduct = ({ index, formik, isSectionDisabled = false, docTypeAadeCode, debugMode = false }: Props) => {
  const { data: formData } = useProductForm();
  const originalSaleCharIdRef = useRef<number | null>(null);
  const product = useMemo(() => formik.values.products[index], [index, formik.values?.products]);
  // const documentTypeId = useMemo(
  //   () => formik.values.documentTypeId?.object?.aadeCode,
  //   [formik.values.documentTypeId?.object?.aadeCode],
  // );
  const prodSaleCatId = useMemo(() => product?.saleCategoryId, [product?.saleCategoryId]);
  const prodSaleCharId = useMemo(() => product?.saleCharacterizationId, [product?.saleCharacterizationId]);
  const saleCategories = useMemo(() => formData?.saleCategories || [], [formData?.saleCategories]);
  const saleCharacterizations = useMemo(() => formData?.saleCharacterizations || [], [formData?.saleCharacterizations]);
  const prodSaleCat = useMemo(
    () => saleCategories?.find((curr) => curr?.id === prodSaleCatId),
    [prodSaleCatId, saleCategories],
  );
  const prodSaleChar = useMemo(
    () => saleCharacterizations?.find((curr) => curr?.id === prodSaleCharId),
    [prodSaleCharId, saleCharacterizations],
  );

  useEffect(() => {
    const res = getProdCharacterizationAadeCodeToSet(docTypeAadeCode, prodSaleCat?.aadeId, prodSaleChar?.aadeCode);
    if (res) {
      // If a saved original exists and is valid for the current doc type, restore it instead of overriding
      if (originalSaleCharIdRef.current !== null) {
        const originalChar = saleCharacterizations?.find((c) => c.id === originalSaleCharIdRef.current);
        if (originalChar && isCharValidForDocType(docTypeAadeCode, prodSaleCat?.aadeId, originalChar.aadeCode)) {
          formik.setFieldValue(`products[${index}].saleCharacterizationId`, originalSaleCharIdRef.current);
          originalSaleCharIdRef.current = null;
          return;
        }
      }
      // Save current characterization before the first override so it can be restored later
      if (originalSaleCharIdRef.current === null && product.saleCharacterizationId != null) {
        originalSaleCharIdRef.current = product.saleCharacterizationId;
      }
      const found = saleCharacterizations?.find((curr) => curr?.aadeCode === res);
      if (found) {
        formik.setFieldValue(`products[${index}].saleCharacterizationId`, found.id);
      }
    }
  }, [docTypeAadeCode, index, prodSaleCat?.aadeId, prodSaleChar?.aadeCode, saleCharacterizations]);

  const getProdInfo1 = useCallback(() => {
    return (
      <div style={{ position: 'absolute', bottom: 64 }}>
        <div className="flex-start-center-row-nowrap flex-gap-6">
          <span className="normalText4 padding-r-10">
            SaleCat: (id: {prodSaleCat?.id}) (aadeId: {prodSaleCat?.aadeId || ''}) - {prodSaleCat?.description || ''}
          </span>
        </div>
      </div>
    );
  }, [prodSaleCat?.aadeId, prodSaleCat?.description, prodSaleCat?.id]);

  const getProdInfo2 = useCallback(() => {
    return (
      <div style={{ position: 'absolute', bottom: 0 }}>
        <div className="flex-start-center-row-nowrap flex-gap-6">
          <span className="normalText4">
            SaleChar: (id: {prodSaleChar?.id}) (aadeCode: {prodSaleChar?.aadeCode || ''}) -{' '}
            {prodSaleChar?.description || ''}
          </span>
        </div>
      </div>
    );
  }, [prodSaleChar?.aadeCode, prodSaleChar?.description, prodSaleChar?.id]);

  const getRemoveButton = () => {
    if (formik.values?.products?.length > 1) {
      return (
        <IonFab className="delete-product">
          <FormikProvider value={formik}>
            <FieldArray
              name="products"
              render={({ remove }) => (
                <IonFabButton disabled={isSectionDisabled} onClick={() => remove(index)} color="transparent">
                  <IonIcon icon={closeCircleOutline} size="large" color="danger" />
                </IonFabButton>
              )}
            />
          </FormikProvider>
        </IonFab>
      );
    }
  };

  const getProductSelect = useCallback(() => {
    return (
      <DocProductSelect
        formik={formik}
        index={index}
        focusOnMount={true}
        selected={product.productId || null}
        isDisabled={isSectionDisabled}
      />
    );
  }, [formik, index, product.productId, isSectionDisabled]);

  // TODO: add floating label to fields to check UI
  return (
    <div
      className="flex-center-start-column-nowrap gap-30-16 mainBackground padding-t-16 padding-b-16 padding-l-16 padding-r-40"
      style={{ position: 'relative' }}>
      <div className="flex-start-start-row-wrap gap-30-16">
        <div className="flex-center-start-column-nowrap">
          <div className="margin-b-6">{getProductSelect()}</div>
          <div>
            <DocProductDescription fieldName="description" formik={formik} index={index} />
          </div>
        </div>
        <DocProductQuantity fieldName="quantity" formik={formik} index={index} />
        <DocProductPrice fieldName="price" formik={formik} index={index} />
        <DocProductDiscount fieldName="discountPercentage" formik={formik} index={index} />
        <DocProductUnit formik={formik} index={index} isDisabled={isSectionDisabled} />
      </div>
      <div className="flex-start-start-row-wrap gap-12">
        <DocProductVat formik={formik} index={index} />
        <DocProductFinalCost formik={formik} index={index} />
      </div>
      {debugMode && getProdInfo1()}
      {debugMode && getProdInfo2()}
      {getRemoveButton()}
    </div>
  );
};
